import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useFlag } from '../../context/FeatureFlagsContext';
import { DeliverySlotPicker, useDeliverySlots, slotPayload } from '../../components/DeliverySlotPicker';
import { FeeLines } from '../../components/OrderSlotSummary';
import { usePoints } from '../../hooks/usePoints';
import { logEvent } from '../../utils/eventsLogger';
import { Navbar } from '../../components/Navbar';
import { BackButton } from '../../components/BackButton';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { CashAppPrepayModal } from '../../components/CashAppPrepayModal';
import { CartInstallNudge } from '../../components/CartInstallNudge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { 
  ShoppingCart, 
  Trash2, 
  Plus, 
  Minus, 
  ArrowLeft,
  CreditCard,
  Banknote,
  Smartphone,
  MapPin,
  Home,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Phase 6.2.2 — addresses can be structured objects OR legacy strings.
// `.trim()` on an object throws TypeError and crashes Place Order silently.
// Always coerce to a display string before using.
function addressToString(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const parts = [value.line1, value.city, value.state, value.postcode].filter(Boolean);
    return parts.join(', ');
  }
  return String(value);
}

// Feb 2026 — Region-not-deliverable bug fix. Legacy customer profiles stored
// truncated address strings like "5731 Glencoe Pl" (no city/state/zip).
// Passing those to checkout fails because the backend needs a zip to resolve
// the region. If the pre-fill string looks incomplete (no US 5-digit zip),
// return "" so the AddressAutocomplete forces the customer to re-pick from
// the dropdown — the picked value flows through as a structured object that
// serializes to a full "line1, city, state, postcode" string.
function addressLooksComplete(strOrObj) {
  if (!strOrObj) return false;
  if (typeof strOrObj === 'object') {
    return !!(strOrObj.line1 && strOrObj.city && strOrObj.state && strOrObj.postcode);
  }
  return /\b\d{5}(?:-\d{4})?\b/.test(String(strOrObj));
}
function safePrefillAddress(value) {
  const flat = addressToString(value);
  return addressLooksComplete(value) ? flat : '';
}

// FastAPI returns Pydantic validation errors as an ARRAY of objects.
// Passing those to sonner toasts crashes (calls .trim() on non-string).
function extractErrorMessage(err, fallback = 'Something went wrong') {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join('; ');
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
}

export function CartPage() {
  const { user } = useAuth();
  const { cartItems, cartTotal, updateQuantity, removeFromCart, placeOrder, isLoading } = useCart();
  const navigate = useNavigate();
  
  const [deliveryAddress, setDeliveryAddress] = useState(safePrefillAddress(user?.address));
  const [addressWasIncomplete, setAddressWasIncomplete] = useState(() => !!user?.address && !addressLooksComplete(user?.address));
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [redeemPts, setRedeemPts] = useState(0);
  const [redeemPreview, setRedeemPreview] = useState(null);
  // Phase 5.5-Lite — Cash App prepay state
  const [prepayOpen, setPrepayOpen] = useState(false);
  const [prepayLoading, setPrepayLoading] = useState(false);
  const [prepayRender, setPrepayRender] = useState(null);
  const [prepayOrderShort, setPrepayOrderShort] = useState('');
  const loyaltyEnabled = useFlag('customer.loyalty', true);
  const redeemEnabled = useFlag('loyalty.redeem', true);
  const { balance: pointsBalance, refresh: refreshPoints } = usePoints();
  const savedAddressesEnabled = useFlag('customer.saved_addresses', true);
  const cashEnabled = useFlag('payment.cash', true);
  const cashAppMaster = useFlag('payment.cash_app', false);
  const cashAppPayNow = useFlag('payment.cashapp_pay_now', false) && cashAppMaster;
  const cashAppOnDelivery = useFlag('payment.cashapp_pay_on_delivery', false) && cashAppMaster;
  const slotsOn = useFlag('customer.scheduled_delivery', false);
  const slotQuote = useDeliverySlots(slotsOn, deliveryAddress);

  // Auto-pick a valid default payment method when flags change
  useEffect(() => {
    const allowed = [];
    if (cashEnabled) allowed.push('cash');
    if (cashAppPayNow) allowed.push('cashapp_now');
    if (cashAppOnDelivery) allowed.push('cashapp_on_delivery');
    if (allowed.length === 0) return;
    if (!allowed.includes(paymentMethod)) {
      setPaymentMethod(allowed[0]);
    }
  }, [cashEnabled, cashAppPayNow, cashAppOnDelivery, paymentMethod]);

  // Fetch saved addresses when opening checkout
  useEffect(() => {
    if (!showCheckout || user?.role !== 'customer' || !savedAddressesEnabled) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/users/me/addresses`, { withCredentials: true });
        const list = data.addresses || [];
        setSavedAddresses(list);
        const def = list.find(a => a.is_default);
        if (def) {
          setSelectedAddressId(def.id);
          // Feb 2026 — same guard as profile-address prefill. If the default
          // saved address is truncated (no zip / missing fields), leave the
          // checkout input empty and surface the nudge so the customer picks
          // a fresh full address from autocomplete.
          const prefill = safePrefillAddress(def.address);
          setDeliveryAddress(prefill);
          if (!addressLooksComplete(def.address)) {
            setAddressWasIncomplete(true);
          }
        }
      } catch (_) {
        setSavedAddresses([]);
      }
    })();
  }, [showCheckout, user?.role, savedAddressesEnabled]);

  const handleSelectSavedAddress = (addressId) => {
    setSelectedAddressId(addressId);
    if (addressId === 'custom') return;
    const a = savedAddresses.find(x => x.id === addressId);
    if (!a) return;
    // Feb 2026 — chip picks route through the same completeness guard so an
    // incomplete saved address can never flow into checkout.
    setDeliveryAddress(safePrefillAddress(a.address));
    setAddressWasIncomplete(!addressLooksComplete(a.address));
  };

  // Live redemption preview as the user types pts
  useEffect(() => {
    if (!loyaltyEnabled || !redeemEnabled || !redeemPts || redeemPts <= 0) {
      setRedeemPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${API_URL}/api/users/me/points/redeem-preview`,
          { pts: Number(redeemPts), order_subtotal: Number(cartTotal) },
          { withCredentials: true }
        );
        setRedeemPreview(data);
      } catch (_) {
        setRedeemPreview({ ok: false, error: 'Could not validate redemption' });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [redeemPts, cartTotal, loyaltyEnabled, redeemEnabled]);

  const handlePlaceOrder = async () => {
    // Phase 6.2.2 hotfix — defensive coercion in case `user.address` flowed
    // through as a structured object. Without this, .trim() throws and the
    // click handler silently crashes (no toast, no failed_orders entry).
    //
    // Feb 2026 follow-up — `addressToString` correctly handles the structured
    // shape from AddressAutocomplete (incl. saved-default), so the empty-check
    // below now fires whether the state is a string OR an object.
    const addr = addressToString(deliveryAddress);
    if (!addr.trim()) {
      toast.error('Please enter a delivery address');
      return;
    }
    // Don't submit if pts entered but preview is invalid
    if (redeemPts > 0 && redeemPreview && !redeemPreview.ok) {
      toast.error(redeemPreview.error || 'Cannot redeem these points');
      return;
    }
    if (slotsOn && !slotQuote.slot) {
      toast.error(slotQuote.none ? 'No delivery slots available right now' : 'Please choose a delivery time slot');
      return;
    }

    // Phase 5.9 — log checkout intent (fire-and-forget)
    logEvent('checkout.started', {
      item_count: cartItems.length,
      subtotal: cartTotal,
      payment_method: paymentMethod,
      redeem_pts: Number(redeemPts) || 0,
    });

    // PAY NOW · CASH APP — show prepay modal first; only submit on confirm.
    if (paymentMethod === 'cashapp_now') {
      try {
        const finalTotal = Math.max(0, cartTotal - Number(redeemPreview?.discount_dollars || 0)) + slotQuote.fee;
        // Use a synthetic short id for the deep link's note — backend will ignore at order create time
        const draftShort = ('DRAFT' + Math.random().toString(36).slice(2, 7)).toUpperCase();
        setPrepayLoading(true);
        setPrepayOpen(true);
        const { data } = await axios.post(
          `${API_URL}/api/payment-render/preview`,
          { amount: finalTotal, draft_order_short: draftShort },
          { withCredentials: true },
        );
        setPrepayRender(data);
        setPrepayOrderShort(draftShort);
      } catch (err) {
        toast.error(extractErrorMessage(err, 'Could not load payment info'));
        setPrepayOpen(false);
      } finally {
        setPrepayLoading(false);
      }
      return;  // Wait for user to confirm in modal
    }

    await submitOrder(paymentMethod);
  };

  const submitOrder = async (methodOverride) => {
    try {
      const order = await placeOrder(
        deliveryAddress,
        methodOverride || paymentMethod,
        notes,
        Number(redeemPts) || 0,
        slotPayload(slotsOn, slotQuote.slot),
      );
      toast.success(
        redeemPts > 0
          ? `Order placed — ${redeemPts} pts redeemed (-$${Number(redeemPreview?.discount_dollars || 0).toFixed(2)})`
          : 'Order placed successfully!'
      );
      refreshPoints();
      setPrepayOpen(false);
      navigate(`/customer/orders/${order.id}`);
    } catch (err) {
      // Phase 5.9.20 — preview-mode block already showed its own toast in
      // CartContext; swallow this re-throw to avoid double toasting.
      if (err?.message === 'PREVIEW_MODE') return;
      const detail = extractErrorMessage(err, 'Failed to place order');
      // Phase 7-lite — surface specific backend reason codes (OUT_OF_STOCK,
      // VARIANT_NOT_CONFIGURED, REGION_NOT_DELIVERABLE, STOCK_RACE, etc.) so
      // customers know exactly what to fix and operators can debug from logs.
      toast.error(detail, { duration: 8000 });
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#050003]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <ShoppingCart className="w-16 h-16 text-[#4B6B52] mx-auto mb-4" />
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold mb-2">CART_EMPTY</h1>
          <p className="text-[#4B6B52] font-mono mb-8">// No items in your cart</p>
          <Link to="/customer">
            <Button className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none" data-testid="continue-shopping">
              <ArrowLeft className="w-4 h-4 mr-2" />
              CONTINUE SHOPPING
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050003]" data-testid="cart-page">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <BackButton to="/customer" />
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text">
            {'>'} SHOPPING_CART
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map(item => (
              <div
                key={item.key}
                className="bg-[#0C0612] border border-[#2A0F3D] p-4 flex gap-4"
                data-testid={`cart-item-${item.key}`}
              >
                <div className="w-20 h-20 bg-[#050003] flex-shrink-0">
                  {item.product.image_url ? (
                    <img
                      src={item.product.image_url}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingCart className="w-8 h-8 text-[#2A0F3D]" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-[#39FF14] font-mono font-bold truncate">{item.product.name}</h3>
                  <p className="text-[#4B6B52] font-mono text-xs uppercase">
                    {item.product.category}{item.size ? ` · ${item.size}` : ''}
                  </p>
                  <p className="text-[#A3C7A8] font-mono text-sm mt-1">${Number(item.unitPrice ?? 0).toFixed(2)} each</p>
                </div>

                <div className="flex flex-col items-end justify-between">
                  <button
                    onClick={() => removeFromCart(item.key)}
                    className="text-[#FF3B30] hover:text-[#FF3B30]/80"
                    data-testid={`remove-item-${item.key}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <div className="flex items-center border border-[#2A0F3D]">
                    <button
                      onClick={() => updateQuantity(item.key, item.quantity - 1)}
                      className="p-2 text-[#A3C7A8] hover:text-[#39FF14] hover:bg-[#2A0F3D]"
                      data-testid={`decrease-${item.key}`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="px-3 text-[#39FF14] font-mono">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.key, item.quantity + 1)}
                      className="p-2 text-[#A3C7A8] hover:text-[#39FF14] hover:bg-[#2A0F3D]"
                      data-testid={`increase-${item.key}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>

                  <span className="text-[#39FF14] font-mono font-bold">
                    ${(item.unitPrice * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary / Checkout */}
          <div className="lg:col-span-1">
            <div className="bg-[#0C0612] border border-[#2A0F3D] p-6 sticky top-24">
              <h2 className="text-[#39FF14] font-mono text-lg font-bold border-b border-[#2A0F3D] pb-4 mb-4">
                ORDER_SUMMARY
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-[#A3C7A8] font-mono text-sm">
                  <span>Subtotal ({cartItems.reduce((sum, i) => sum + i.quantity, 0)} items)</span>
                  <span>${Number(cartTotal ?? 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#A3C7A8] font-mono text-sm">
                  <span>Delivery Fee</span>
                  {slotsOn ? (
                    <span className="text-[#39FF14]" data-testid="cart-delivery-fee">
                      {slotQuote.slot ? (slotQuote.fee > 0 ? `$${slotQuote.fee.toFixed(2)}` : 'FREE') : 'PICK SLOT'}
                    </span>
                  ) : (
                    <span className="text-[#39FF14]">FREE</span>
                  )}
                </div>
                {slotsOn && slotQuote.slot && (
                  <FeeLines lines={slotQuote.slot.fee_lines} waived={slotQuote.slot.fee_waived} tier={slotQuote.tier} testId="cart-fee-lines" />
                )}
                {loyaltyEnabled && redeemEnabled && pointsBalance > 0 && (
                  <div className="border-t border-[#2A0F3D] pt-3" data-testid="redeem-block">
                    <Label className="text-[#FF9F0A] font-mono text-xs uppercase tracking-wider flex items-center gap-2 mb-2">
                      <Sparkles className="w-3 h-3" />
                      REDEEM POINTS · {pointsBalance.toLocaleString()} AVAILABLE
                    </Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="number"
                        min={0}
                        max={pointsBalance}
                        value={redeemPts}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(Number(e.target.value || 0), pointsBalance));
                          setRedeemPts(v);
                        }}
                        placeholder="0"
                        className="bg-[#050003] border-[#2A0F3D] text-[#FF9F0A] font-mono focus:border-[#FF9F0A] rounded-none w-32"
                        data-testid="redeem-pts-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setRedeemPts(pointsBalance)}
                        className="border-[#2A0F3D] text-[#A3C7A8] hover:border-[#FF9F0A] hover:text-[#FF9F0A] rounded-none font-mono text-xs"
                        data-testid="redeem-max-btn"
                      >
                        MAX
                      </Button>
                      {redeemPts > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setRedeemPts(0)}
                          className="text-[#4B6B52] hover:text-[#FF3B30] font-mono text-xs"
                          data-testid="redeem-clear-btn"
                        >
                          CLEAR
                        </Button>
                      )}
                    </div>
                    {redeemPts > 0 && redeemPreview && (
                      <p
                        className={`mt-2 font-mono text-xs ${redeemPreview.ok ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}
                        data-testid="redeem-preview"
                      >
                        {redeemPreview.ok
                          ? `// −$${Number(redeemPreview.discount_dollars).toFixed(2)} off`
                          : `// ${redeemPreview.error}`}
                      </p>
                    )}
                  </div>
                )}
                {redeemPts > 0 && redeemPreview?.ok && (
                  <div className="flex justify-between text-[#FF9F0A] font-mono text-sm">
                    <span>Points Discount ({redeemPts} pts)</span>
                    <span>-${Number(redeemPreview.discount_dollars).toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-[#2A0F3D] pt-3 flex justify-between text-[#39FF14] font-mono font-bold">
                  <span>TOTAL</span>
                  <span data-testid="cart-total-final">
                    ${((redeemPts > 0 && redeemPreview?.ok
                        ? Math.max(0, cartTotal - Number(redeemPreview.discount_dollars))
                        : cartTotal
                      ) + slotQuote.fee).toFixed(2)}
                  </span>
                </div>
              </div>

              {!showCheckout ? (
                <>
                  {/* Phase 6.0.7 — cart install nudge with $5 (100 pts)
                      bonus. Highest-intent install moment in the funnel.
                      Hidden if already standalone or dismissed in the last
                      7 days. */}
                  <CartInstallNudge cartItemCount={cartItems.length} />
                  <Button 
                    onClick={() => setShowCheckout(true)}
                    className="w-full bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] hover:shadow-[0_0_12px_rgba(57,255,20,0.6)] rounded-none h-12"
                    data-testid="proceed-checkout"
                  >
                    PROCEED TO CHECKOUT
                  </Button>
                </>
              ) : (
                <div className="space-y-4">
                  {/* Delivery Address */}
                  <div className="space-y-2">
                    <Label className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      DELIVERY_ADDRESS
                    </Label>
                    {/* Saved-address quick picker */}
                    {savedAddressesEnabled && savedAddresses.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                        {savedAddresses.map(a => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => handleSelectSavedAddress(a.id)}
                            className={`p-2 border text-left font-mono text-xs transition-colors ${
                              selectedAddressId === a.id
                                ? 'border-[#39FF14] bg-[rgba(57,255,20,0.08)] text-[#39FF14]'
                                : 'border-[#2A0F3D] bg-[#050003] text-[#A3C7A8] hover:border-[#39FF14]'
                            }`}
                            data-testid={`saved-addr-pick-${a.id}`}
                          >
                            <div className="flex items-center gap-1">
                              <Home className="w-3 h-3 shrink-0" />
                              <span className="truncate font-bold">{a.label}</span>
                            </div>
                            <p className="text-[10px] text-[#4B6B52] truncate mt-0.5">{addressToString(a.address)}</p>
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => handleSelectSavedAddress('custom')}
                          className={`p-2 border text-left font-mono text-xs transition-colors ${
                            selectedAddressId === 'custom'
                              ? 'border-[#FF9F0A] bg-[rgba(255,159,10,0.08)] text-[#FF9F0A]'
                              : 'border-[#2A0F3D] bg-[#050003] text-[#6B8E73] hover:border-[#FF9F0A]'
                          }`}
                          data-testid="use-custom-address"
                        >
                          <span className="font-bold">+ NEW</span>
                          <p className="text-[10px] mt-0.5">One-time</p>
                        </button>
                      </div>
                    )}
                    {addressWasIncomplete && (
                      <div
                        className="mb-2 p-2.5 border border-[#FF9F0A] bg-[rgba(255,159,10,0.08)] text-[#FF9F0A] font-mono text-[11px]"
                        data-testid="address-incomplete-nudge"
                      >
                        Your saved address is missing city / zip. Please pick a
                        fresh address from the suggestions so we can deliver to
                        the right place.
                      </div>
                    )}
                    <AddressAutocomplete
                      value={deliveryAddress}
                      onChange={setDeliveryAddress}
                      placeholder="Start typing your address..."
                      data-testid="delivery-address"
                    />
                  </div>

                  {slotsOn && <DeliverySlotPicker quote={slotQuote} />}

                  {/* Payment Method */}
                  <div className="space-y-2">
                    <Label className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                      PAYMENT_METHOD
                    </Label>
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2">
                      {cashEnabled && (
                        <div className="flex items-center space-x-3 bg-[#050003] border border-[#2A0F3D] p-3 hover:border-[#39FF14] cursor-pointer" onClick={() => setPaymentMethod('cash')}>
                          <RadioGroupItem value="cash" id="cash" className="border-[#39FF14] text-[#39FF14]" data-testid="payment-cash" />
                          <Label htmlFor="cash" className="flex items-center gap-2 text-[#A3C7A8] font-mono text-sm cursor-pointer">
                            <Banknote className="w-4 h-4" />
                            Cash on Delivery
                          </Label>
                        </div>
                      )}
                      {cashAppPayNow && (
                        <div className="flex items-center space-x-3 bg-[#050003] border border-[#2A0F3D] p-3 hover:border-[#39FF14] cursor-pointer" onClick={() => setPaymentMethod('cashapp_now')}>
                          <RadioGroupItem value="cashapp_now" id="cashapp_now" className="border-[#39FF14] text-[#39FF14]" data-testid="payment-cashapp-now" />
                          <Label htmlFor="cashapp_now" className="flex items-center gap-2 text-[#A3C7A8] font-mono text-sm cursor-pointer">
                            <Smartphone className="w-4 h-4" />
                            Pay Now · Cash App
                          </Label>
                        </div>
                      )}
                      {cashAppOnDelivery && (
                        <div className="flex items-center space-x-3 bg-[#050003] border border-[#2A0F3D] p-3 hover:border-[#39FF14] cursor-pointer" onClick={() => setPaymentMethod('cashapp_on_delivery')}>
                          <RadioGroupItem value="cashapp_on_delivery" id="cashapp_on_delivery" className="border-[#39FF14] text-[#39FF14]" data-testid="payment-cashapp-on-delivery" />
                          <Label htmlFor="cashapp_on_delivery" className="flex items-center gap-2 text-[#A3C7A8] font-mono text-sm cursor-pointer">
                            <Smartphone className="w-4 h-4" />
                            Pay on Delivery · Cash App
                          </Label>
                        </div>
                      )}
                    </RadioGroup>
                  </div>

                  {/* Order Notes */}
                  <div className="space-y-2">
                    <Label className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                      ORDER_NOTES (Optional)
                    </Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none resize-none"
                      placeholder="Special instructions..."
                      rows={2}
                      data-testid="order-notes"
                    />
                  </div>

                  <Button 
                    onClick={handlePlaceOrder}
                    disabled={isLoading || (slotsOn && slotQuote.none)}
                    className="w-full bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] hover:shadow-[0_0_12px_rgba(57,255,20,0.6)] rounded-none h-12"
                    data-testid="place-order"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="cursor-blink">█</span> PROCESSING...
                      </span>
                    ) : (
                      '> PLACE ORDER'
                    )}
                  </Button>

                  <Button 
                    variant="outline"
                    onClick={() => setShowCheckout(false)}
                    className="w-full border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none"
                    data-testid="back-to-cart"
                  >
                    BACK TO CART
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <CashAppPrepayModal
        open={prepayOpen}
        onClose={() => setPrepayOpen(false)}
        amount={Math.max(0, cartTotal - Number(redeemPreview?.discount_dollars || 0)) + slotQuote.fee}
        orderShortId={prepayOrderShort}
        render={prepayRender}
        loading={prepayLoading}
        onConfirmPaid={() => submitOrder('cashapp_now')}
        onPayOnDelivery={() => {
          setPrepayOpen(false);
          if (cashAppOnDelivery) {
            setPaymentMethod('cashapp_on_delivery');
            toast.info('Switched to Pay on Delivery — placing order…');
            submitOrder('cashapp_on_delivery');
          } else {
            setPaymentMethod('cash');
            toast.info('Switched to Cash on Delivery — placing order…');
            submitOrder('cash');
          }
        }}
      />
    </div>
  );
}
